import { useEffect, useRef, useState } from 'react'
import { HeaderNotificationsMenu, HeaderUserMenu } from '../../../partials'
import ChangePassword from '../../../../app/modals/change-password'
import { useAuth } from '../../../../app/modules/auth'
import { NOTIFICATION } from '../../../../api/apiEndPoints'
import APICallService from '../../../../api/apiCallService'
import notifIcon from '../../../assets/media/svg_supplier/mingcute_notification-fill.svg'
import whatsappGreenIcon from '../../../assets/media/svg_supplier/whatsapp-green.svg'
import emailBlueIcon from '../../../assets/media/svg_supplier/email-blue.svg'
import logo from '../../../assets/media/svg_supplier/Logo.svg'

const SUPPORT_PHONE = '+255 770212121'
const SUPPORT_EMAIL = 'usupport@uworld.tz'
const WHATSAPP_URL = `https://wa.me/${SUPPORT_PHONE.replace(/\D/g, '')}`
const EMAIL_URL = `mailto:${SUPPORT_EMAIL}`
const HEADER_ICON_CLASS = 'block h-8 w-8 object-contain'

const getUnreadCount = (unreadItems?: number[]) => {
  if (!unreadItems?.length) return 0
  const typeCount = unreadItems.filter((item) => item !== -2).length
  return typeCount || 1
}

const Navbar = () => {
  const { currentUser } = useAuth()
  const [showNotif, setShowNotif] = useState(false)
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [unreadCount, setUnreadCount] = useState(0)

  const userMenuRef = useRef<HTMLDivElement>(null)
  const notifRef = useRef<HTMLDivElement>(null)
  const profileImage = currentUser?.business?.image
  const supplierName = currentUser?.name || currentUser?.business?.ownerName
  const contactPersonName = currentUser?.business?.name
  const welcomeName =
    contactPersonName && supplierName
      ? `${contactPersonName} (${supplierName})`
      : contactPersonName || supplierName || ''
  const fetchNotificationCount = async () => {
    if (!currentUser?.business?.profile) return
    const apiService = new APICallService(
      NOTIFICATION.GETNOTFICATIONCOUNT,
      currentUser.business.profile
    )
    const response = await apiService.callAPI()
    if (response) {
      setUnreadCount(getUnreadCount(response.unreadItems))
    } else {
      setUnreadCount(0)
    }
  }

  const handleMarkAllAsRead = async () => {
    if (!currentUser?.business?.profile) return
    const apiService = new APICallService(NOTIFICATION.MARKREAD, null, {
      id: currentUser.business.profile,
    })
    const response = await apiService.callAPI()
    if (response) {
      setUnreadCount(0)
    }
  }

  useEffect(() => {
    fetchNotificationCount()
  }, [currentUser?.business?.profile])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node
      if (userMenuRef.current && !userMenuRef.current.contains(target)) {
        setShowUserMenu(false)
      }
      if (notifRef.current && !notifRef.current.contains(target)) {
        setShowNotif(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <div className='sticky top-0 z-[100] flex h-20 w-full min-w-0 items-center justify-between bg-white px-5'>
      <ChangePassword show={showPasswordModal} onHide={() => setShowPasswordModal(false)} />
      {/* Logo + Welcome message - left */}
      <div className='flex min-w-0 flex-1 items-center gap-3'>
        <img src={logo} alt='uProcure Logo' className='h-[34px] w-[52px] shrink-0' />
        <div style={{ width: '1px', height: '34px', background: 'var(--Yellow, #F6C302)' }} className='shrink-0' />
        <span className='truncate text-base font-bold text-[20px] text-gray-900 sm:text-[20px]'>
          Welcome back, {welcomeName} 👋🏻
        </span>
      </div>

      {/* Right - contact icons, bell, profile */}
      <div className='flex min-w-0 flex-1 items-center justify-end gap-3'>
        <div style={{ width: '1px', height: '34px', background: 'var(--Yellow, #F6C302)' }} />
        <div className='flex shrink-0 items-center gap-3'>
          {/* WhatsApp */}
          <div className='flex items-center justify-center bg-[#F7F7F7] border-r10px'>
            <a
              href={WHATSAPP_URL}
              target='_blank'
              rel='noopener noreferrer'
              className='relative p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors'
              aria-label='WhatsApp support'
              onClick={() => setShowNotif(false)}
            >
              <img src={whatsappGreenIcon} className={HEADER_ICON_CLASS} alt='WhatsApp support' />
            </a>
          </div>

          {/* Email */}
          <div className='flex items-center justify-center bg-[#F7F7F7] border-r10px'>
            <a
              href={EMAIL_URL}
              className='relative p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 border-r10px transition-colors'
              aria-label='Email support'
              onClick={() => setShowNotif(false)}
            >
              <img src={emailBlueIcon} className={HEADER_ICON_CLASS} alt='Email support' />
            </a>
          </div>

          {/* Bell */}
          <div
            ref={notifRef}
            className='relative flex items-center justify-center bg-[#F7F7F7] border-r10px'
          >
            <button
              type='button'
              className='relative p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 border-r10px transition-colors'
              aria-label='Notifications'
              onClick={() => setShowNotif(!showNotif)}
            >
              <img src={notifIcon} className={HEADER_ICON_CLASS} alt='Notifications' />
              {unreadCount > 0 && (
                <span className='absolute -top-0.5 -right-0.5 flex h-5 min-w-[20px] items-center justify-center bg-green-500 px-1 text-[10px] font-bold leading-none text-white border-r10px'>
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>
            <HeaderNotificationsMenu
              show={showNotif}
              unreadCount={unreadCount}
              onMarkAllAsRead={handleMarkAllAsRead}
            />
          </div>

          <div className='relative w-[71px]' ref={userMenuRef}>
            <button
              className="flex items-center justify-center w-14 h-14 bg-blue-50 cursor-pointer hover:bg-blue-100 transition-colors overflow-hidden border-r10px"
              onClick={() => setShowUserMenu(!showUserMenu)}
            >
              {profileImage ? (
                <img
                  src={profileImage}
                  alt={currentUser?.name || 'Profile'}
                  className='w-full h-full object-cover'
                />
              ) : (
                <span className='text-blue-600 font-bold text-base'>
                  {currentUser?.name?.charAt(0).toUpperCase()}
                </span>
              )}
            </button>
            {showUserMenu && (
              <HeaderUserMenu
                onChangePassword={() => {
                  setShowUserMenu(false)
                  setShowPasswordModal(true)
                }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export { Navbar }
